import axios, { AxiosRequestConfig } from "axios";
import { navigateTo } from "./NavigationService";

type ApiResponseType = {
  status: boolean;
  message: string;
  data: any;
};

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL, // Replace with your API base URL
  // timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // taking auth token from local Storage
    if (token && token != "null") {
      const { token_type, access_token } = JSON.parse(token);
      config.headers.Authorization = `${token_type} ${access_token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes("/v1/auth/logout")
    ) {
      Promise.all([import("../store"), import("../store/authSlice")]).then(
        ([{ store }, { logoutUser }]) => {
          store.dispatch(logoutUser()).then(() => navigateTo("/"));
        },
      );
    }

    return Promise.reject(error);
  },
);

const apiRequest = async (
  method: "get" | "post" | "put" | "patch" | "delete",
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponseType> => {
  const response = await apiClient({ method, url, data, ...config });
  return response.data;
};

// Specific API calls using the centralized request function:
export const Login = (data: {
  email: string;
  password: string;
  store_id: string;
}) => apiRequest("post", "/v1/auth/login", data);

export const Logout = () => apiRequest("post", "/v1/auth/logout");

export const Register = (data: any) =>
  apiRequest("post", "/v1/auth/register", data);

export const ForgotPassword = (data: { store_id: string; email: string }) =>
  apiRequest("post", "/v1/auth/forgot-password", data);

export const ResetPassword = (data: any) =>
  apiRequest("post", "/v1/auth/reset-password", data);

export const SocialLogin = (data: any) =>
  apiRequest("post", "/v1/auth/social/login", data);

export const AppleAuthorization = (data: any) =>
  apiRequest("post", "/v1/auth/apple", data);

export const GetUserApi = (data: { store_id: string; customer_id?: string }) =>
  apiRequest("post", "/v1/user", data);

export const GetStoreMenu = (data: {
  store_id: string | number;
  menu_id?: string | number;
}) => apiRequest("post", "/v1/store/menu", data);

export const GetStoreLocations = (data: { store_id: any }) =>
  apiRequest("post", "/v1/store/locations", data);

export const GetStoreCoupons = (data: { store_id: number | string }) =>
  apiRequest("post", "/v1/store/coupons", data);

export const fetchStoreCoupons = async (postData: { store_id: string }) => {
  const { status, data } = await apiRequest(
    "post",
    "/v1/store/coupons",
    postData,
  );
  if (status) return data;
  throw new Error("Failed to fetch coupons");
};

export const GetOrders = (data: {
  store_id: any;
  customer_id: any;
  is_favourite?: boolean;
  page?: number;
}) => apiRequest("post", "/v1/orders", data);

export const GetActiveOrders = (data: { store_id: any; customer_id: any }) =>
  apiRequest("post", "/v1/orders/active", data);

export const GetOrderById = (data: {
  store_id: any;
  customer_id: any;
  order_id: any;
}) => apiRequest("post", "/v1/orderdetails", data);

export const GetReorderData = (data: {
  store_id: any;
  order_id: any;
  customer_id: any;
}) => apiRequest("post", "/v1/reorder", data);

export const MarkOrderFavApi = (data: any) =>
  apiRequest("post", "/v1/order/addtofav", data);

export const GetSavedPayments = (data: {
  store_id: number | string;
  customer_id: number | string;
}) => apiRequest("post", "/v1/user/payments", data);

export const DeleteSavedPayment = (data: any) =>
  apiRequest("delete", "/v1/user/payments", data);

export const GetSavedAddresses = (data: {
  customer_id: any;
  address_type: any;
}) => apiRequest("post", "/v1/user/address", data);

export const StoreCustomerAddress = (data: any) =>
  apiRequest("post", "/v1/user/address/store", data);

export const UpdateCustomerAddress = (data: any) =>
  apiRequest("post", "/v1/user/address/update", data);

export const DeleteCustomerAddress = (data: any) =>
  apiRequest("delete", "/v1/user/address/destroy", data);

export const GetUserLoyaltyData = (data: { store_id: any; customer_id: any }) =>
  apiRequest("post", "/v1/user/loyalty", data);

export const GetUserLoyaltyOrdersApi = (data: {
  store_id: any;
  customer_id: any;
}) => apiRequest("post", "/v1/user/loyalty/orders", data);

export const CreateCart = (data: any) =>
  apiRequest("post", "/v1/cart/store", data);

export const ValidateCart = (data: any) =>
  apiRequest("post", "/v1/cart/validate", data);

export const UpdateCart = (data: any) =>
  apiRequest("post", "/v1/cart/update", data);

export const ApplyCoupon = (data: any) =>
  apiRequest("post", "/v1/cart/applycoupon", data);

export const GetPaymentApi = (data: any) =>
  apiRequest("post", "/v1/cart/payment", data);

export const CheckoutApi = (data: any) =>
  apiRequest("post", "/v1/cart/checkout", data);

export const UpdateUserProfileApi = (data: any) =>
  apiRequest("post", "/v1/user/profile/update", data);

export const DeleteUserProfileApi = (data: any) =>
  apiRequest("delete", "/v1/user/destroy", data);

export const ValidateGiftcard = (data: any) =>
  apiRequest("post", "/v1/giftcard/validate", data);
